import { DeviceTypes, type DeviceType } from '@/libs/utils/device'
import { useStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { ref } from 'vue'

export type AvailableLanguages = 'english'
export type AvailableThemes = 'Dark' | 'Light' | 'System'
export type ResolvedThemes = 'Dark' | 'Light'
export type AvailableItemViewStyles = 'grid' | 'list'

export type TermsInspectionSettings = {
  reviewConfirmation: boolean
  showIntro: boolean
}

export type Settings = {
  language: AvailableLanguages
  theme: AvailableThemes
  resolved_theme?: ResolvedThemes
  itemViewStyle: AvailableItemViewStyles
  device: DeviceType
}

const defaultSettings: Settings = {
  language: 'english',
  theme: 'System',
  resolved_theme: undefined,
  itemViewStyle: 'list',
  device: DeviceTypes.DESKTOP,
}

function patch_store(
  store: Record<string, unknown>,
  defaults: Record<string, any> | unknown[],
) {
  try {
    if (store === undefined || store === null) {
      return defaults
    } else if (typeof store !== 'object') {
      return store
    }

    if (!Array.isArray(defaults)) {
      const keys = Object.keys(defaults)
      for (let i = 0; i < keys.length; i++) {
        const key = keys[i]
        if (!(key in store)) {
          if (Array.isArray(defaults[key])) {
            store[key] = defaults[key]
          } else {
            store[key] = patch_store({}, defaults[key])
          }
        }
      }
    }
    return store
  } catch (e) {
    console.error(e)
    return defaults
  }
}

export const useSettingsStore = defineStore('settings', () => {
  const isFooterVisible = ref(true)
  const settings = useStorage('settingsStore', defaultSettings, localStorage, {
    writeDefaults: true,
    mergeDefaults: (storageValue, defaults) => {
      return patch_store(storageValue, defaults) as Settings
    },
  })

  function setLanguage(value: AvailableLanguages) {
    settings.value.language = value
  }
  function setItemViewStyle(value: AvailableItemViewStyles) {
    settings.value.itemViewStyle = value
  }
  function setTheme(value: AvailableThemes) {
    console.log('Theme: ', value)
    settings.value.theme = value
    let resolved_theme: ResolvedThemes
    if (value === 'System') {
      const system_theme = window.matchMedia('(prefers-color-scheme: dark)')
        .matches
        ? 'Dark'
        : 'Light'
      resolved_theme = system_theme
    } else {
      resolved_theme = value
    }
    const prev_theme = settings.value.resolved_theme
    const [html] = document.getElementsByTagName('html')

    if (resolved_theme === 'Dark') {
      html.classList.add('dark')
    } else {
      html.classList.remove('dark')
    }

    settings.value.resolved_theme = resolved_theme
  }
  function setDevice(device: DeviceType) {
    settings.value.device = device
  }
  function setFooterVisibility(value: boolean) {
    isFooterVisible.value = value
  }

  setTheme(settings.value.theme)

  window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', () => {
      if (settings.value.theme === 'System') {
        setTheme('System')
      }
    })

  return {
    settings,
    isFooterVisible,
    setLanguage,
    setTheme,
    setItemViewStyle,
    setDevice,
    setFooterVisibility,
  }
})
